<script setup lang="ts">
import { computed } from 'vue'

import { channelLabelKey } from '../../service/channelLabels'
import type { DataSyncEntry } from '../../service/dataSync'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import {
  Icon,
  PlatformLogo,
  resolveStaticPlatformLogo,
  Tooltip,
  type PlatformLogoProps,
} from '@/shared/ui'

const props = defineProps<{
  title: string
  /** Caller-localised info tooltip (v3 OrphexAdditionalInfoTitle description). */
  description?: string | undefined
  /** Per-channel freshness stamps (v3 OrphexAdditionalInfoTitle raw-data tooltip). */
  syncEntries?: DataSyncEntry[] | undefined
  tabs?: string[]
  titleClass?: string
  tabsClass?: string
}>()

const { t, te } = useI18n()
const format = useFormat()

/** Explicit `description` wins over sync data — same precedence as v3's `getRawInfo`. */
const showSyncTooltip = computed(() => !props.description && (props.syncEntries?.length ?? 0) > 0)
const hasTooltip = computed(() => Boolean(props.description) || showSyncTooltip.value)

function dataSourceLabel(dataSource: string): string {
  const key = channelLabelKey(dataSource)
  return key && te(key) ? t(key) : dataSource
}

// `PlatformLogoProps.src` is `string | undefined` under `exactOptionalPropertyTypes` — the
// resolved logo can only be spread in when present (same shape as `platformLogoProps.ts`).
function logoProps(dataSource: string): PlatformLogoProps {
  const src = resolveStaticPlatformLogo(dataSource)
  return { platform: dataSource, size: 14, ...(src ? { src } : {}) }
}
</script>

<template>
  <div class="report-section-title flex min-w-0 flex-wrap items-center gap-xs">
    <h2 class="m-0 text-fg text-xl font-bold" :class="titleClass">{{ title }}</h2>
    <Tooltip v-if="hasTooltip" :text="description ?? ''">
      <template v-if="showSyncTooltip" #content>
        <div class="report-section-title__sync flex flex-col gap-2xs">
          <h3 class="text-surface m-0 text-xs font-semibold">
            {{ t('reports.section.dataSync') }}
          </h3>
          <p
            v-for="entry in syncEntries"
            :key="entry.dataSource"
            class="m-0 flex items-center gap-2xs"
          >
            <PlatformLogo v-bind="logoProps(entry.dataSource)" />
            <span class="font-semibold">{{ `${dataSourceLabel(entry.dataSource)}:` }}</span>
            {{ format.formatDateTime(entry.lastModified) }}
          </p>
        </div>
      </template>
      <Icon name="info" :size="14" :label="description ?? t('reports.section.dataSync')" />
    </Tooltip>
    <div
      v-if="tabs?.length"
      class="report-section-title__tabs flex flex-wrap gap-2xs"
      :class="tabsClass"
    >
      <span
        v-for="tab in tabs"
        :key="tab"
        class="rounded-full bg-surface-sunken px-2 py-0.5 text-[0.6875rem] text-muted"
        >{{ tab }}</span
      >
    </div>
  </div>
</template>
